import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import '@/App.css';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Zap, AlertTriangle, Loader2, Smartphone, Monitor, ArrowRight } from 'lucide-react';
import { SEOHead } from './components/SEOHead';
import { AuthOnboardingCard } from './components/AuthOnboardingCard';
const PerformanceGauge = lazy(() => import('./components/PerformanceGauge').then(m => ({ default: m.PerformanceGauge })));
const MetricCard = lazy(() => import('./components/MetricCard').then(m => ({ default: m.MetricCard })));
const TopFixCard = lazy(() => import('./components/TopFixCard').then(m => ({ default: m.TopFixCard })));
const NextFixCard = lazy(() => import('./components/NextFixCard').then(m => ({ default: m.NextFixCard })));
const LeadCaptureCard = lazy(() => import('./components/LeadCaptureCard').then(m => ({ default: m.LeadCaptureCard })));
const AnalysisProgress = lazy(() => import('./components/AnalysisProgress').then(m => ({ default: m.AnalysisProgress })));
const SiteContextModal = lazy(() => import('./components/SiteContextModal').then(m => ({ default: m.SiteContextModal })));
const SnippetModal = lazy(() => import('./components/SnippetModal').then(m => ({ default: m.SnippetModal })));
const LoadingSnippetModal = lazy(() => import('./components/SnippetModal').then(m => ({ default: m.LoadingSnippetModal })));
const FAQSection = lazy(() => import('./components/FAQSection').then(m => ({ default: m.FAQSection })));
const ResultPreview = lazy(() => import('./components/ResultPreview').then(m => ({ default: m.ResultPreview })));
import { Toaster, toast } from 'sonner';
import { supabaseClient, supabaseConfigured } from './lib/supabase';
import { 
  initGA, 
  trackAnalysisStart, 
  trackAnalysisComplete, 
  trackAnalysisError,
  trackDraftPrOpened,
  trackGitHubConnectStarted,
  trackImplementationCtaClicked,
  trackImplementationJobCompleted,
  trackImplementationJobCreated,
  trackImplementationJobFailed,
  trackImplementationJobSkipped,
  trackPageView,
  trackTabSwitch,
  trackSnippetGenerated,
  trackEvent
} from './utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('mobile');
  const [showContextModal, setShowContextModal] = useState(false);
  const [siteContext, setSiteContext] = useState({ siteType: null, techStack: null });
  const [detectedTech, setDetectedTech] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [techDetectionMessage, setTechDetectionMessage] = useState('');
  const [snippetModal, setSnippetModal] = useState({ isOpen: false, snippet: '', fixTitle: '', techStack: '', contentKind: 'snippet' });
  const [loadingSnippet, setLoadingSnippet] = useState(false);
  const [loadingSnippetKind, setLoadingSnippetKind] = useState('snippet');
  const [implementationCandidates, setImplementationCandidates] = useState({});
  const [implementationLoading, setImplementationLoading] = useState(false);
  const [implementationJobs, setImplementationJobs] = useState({});
  const [loadingHistoryAnalysis, setLoadingHistoryAnalysis] = useState(false);
  const [creatingImplementationKey, setCreatingImplementationKey] = useState(null);
  const [implementationLimitReached, setImplementationLimitReached] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadNote, setLeadNote] = useState('');
  const [leadCaptureSubmitting, setLeadCaptureSubmitting] = useState(false);
  const [leadCaptureSubmitted, setLeadCaptureSubmitted] = useState(false);
  const [refreshingLiveData, setRefreshingLiveData] = useState(false);
  const [syncingInstallation, setSyncingInstallation] = useState(false);
  const [githubConnecting, setGithubConnecting] = useState(false);
  const [refreshingGithub, setRefreshingGithub] = useState(false);
  const [manualInstallationId, setManualInstallationId] = useState('');
  const [githubConfigStatus, setGithubConfigStatus] = useState(null);
  const [showRepositoryManager, setShowRepositoryManager] = useState(false);
  const [showDeveloperTools, setShowDeveloperTools] = useState(false);
  const trackedImplementationEventsRef = useRef(new Set());
  const [processingTime, setProcessingTime] = useState(null); // { phase1Ms, totalMs }
  const analysisAbortRef = useRef(null);
  const pendingContextRef = useRef(null); // resolves when modal submits
  const githubStateLoadedRef = useRef(false);
  const [githubState, setGithubState] = useState({
    configured: false,
    installUrl: null,
    appSlug: null,
    connected: false,
    repositories: [],
    selectedRepository: ''
  });
  const [authReady, setAuthReady] = useState(!supabaseConfigured);
  const [authLoading, setAuthLoading] = useState(false);
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);
  const [accountContext, setAccountContext] = useState({
    accessToken: null,
    user: null,
    membership: null,
    tenant: null,
    needsOnboarding: false,
    isNewUser: false
  });
  const accountContextRef = useRef(accountContext);
  useEffect(() => { accountContextRef.current = accountContext; }, [accountContext]);
  const refreshGitHubStateRef = useRef(null);
  const [onboardingForm, setOnboardingForm] = useState({
    workspaceName: '',
    primaryEmail: ''
  });
  const [userHistory, setUserHistory] = useState({ analyses: [], jobs: [], total: null, allLoaded: false });
  const [userHistoryLoading, setUserHistoryLoading] = useState(false);
  const [userHistoryLoadingMore, setUserHistoryLoadingMore] = useState(false);
  const [userJobsLoading, setUserJobsLoading] = useState('idle'); // 'idle' | 'loading' | 'done'
  const [userUsage, setUserUsage] = useState(null);
  const [analysisError, setAnalysisError] = useState(null); // { message, type }
  const installSyncAttemptedRef = useRef(false);

  useEffect(() => {
    initGA();
    trackPageView('/');

    const handleStorageSync = (e) => {
      if (e.key === 'github_install_synced') {
        refreshGitHubState(accountContextRef.current?.accessToken);
      }
    };
    window.addEventListener('storage', handleStorageSync);
    return () => window.removeEventListener('storage', handleStorageSync);
  }, []);

  const fetchUserHistoryAndUsage = async (accessToken) => {
    if (!accessToken) return;
    setUserHistoryLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const [analysesRes, metaRes, usageRes] = await Promise.allSettled([
        axios.get(`${API}/user/analyses?limit=5&offset=0`, { headers }),
        axios.get(`${API}/user/analyses/meta`, { headers }),
        axios.get(`${API}/user/usage`, { headers }),
      ]);
      const analyses = analysesRes.status === 'fulfilled' ? (analysesRes.value.data.analyses || []) : [];
      const total = metaRes.status === 'fulfilled' ? (metaRes.value.data.total ?? null) : null;
      setUserHistory((prev) => ({
        ...prev,
        analyses: analysesRes.status === 'fulfilled' ? analyses : prev.analyses,
        total,
        allLoaded: total !== null && analyses.length >= total,
      }));
      if (usageRes.status === 'fulfilled') {
        setUserUsage(usageRes.value.data);
      }
      // Load Draft PR jobs in background after analyses are shown
      setUserJobsLoading('loading');
      axios.get(`${API}/user/jobs`, { headers })
        .then((res) => setUserHistory((prev) => ({ ...prev, jobs: res.data.jobs || [] })))
        .catch(() => {})
        .finally(() => setUserJobsLoading('done'));
    } catch (error) {
      console.error('Failed to fetch user history:', error);
    } finally {
      setUserHistoryLoading(false);
      // Always load GitHub state after history attempt, success or failure
      refreshGitHubStateRef.current?.(accessToken);
    }
  };

  const loadMoreAnalyses = async () => {
    if (!accountContext.accessToken || userHistoryLoadingMore) return;
    setUserHistoryLoadingMore(true);
    try {
      const headers = { Authorization: `Bearer ${accountContext.accessToken}` };
      const offset = userHistory.analyses.length;
      const res = await axios.get(`${API}/user/analyses?limit=5&offset=${offset}`, { headers });
      const newAnalyses = res.data.analyses || [];
      setUserHistory((prev) => {
        const merged = [...prev.analyses, ...newAnalyses.filter((a) => !prev.analyses.some((p) => p.analysis_id === a.analysis_id))];
        return { ...prev, analyses: merged, allLoaded: merged.length >= (prev.total ?? merged.length) };
      });
    } catch (error) {
      console.error('Failed to load more analyses:', error);
    } finally {
      setUserHistoryLoadingMore(false);
    }
  };

  const handleLoadAnalysis = async (analysis) => {
    if (!accountContext.accessToken) return;
    setLoadingHistoryAnalysis(true);
    try {
      // Fetch full analysis data (summary rows in the list omit large text fields)
      let fullAnalysis = analysis;
      if (!(analysis.recommendations?.[0]?.how_to_fix_steps)) {
        try {
          const res = await axios.get(`${API}/user/analyses/${analysis.analysis_id}`, {
            headers: { Authorization: `Bearer ${accountContext.accessToken}` },
          });
          fullAnalysis = res.data;
        } catch {
          // fall back to whatever we have
        }
      }
      // Reconstruct a result object from the stored analysis
      const recs = fullAnalysis.recommendations || [];
      const mobileRecs = recs.filter((r) => r.strategy === 'mobile');
      const desktopRecs = recs.filter((r) => r.strategy === 'desktop');
      const mobileTop = mobileRecs.find((r) => r.recommendation_key?.endsWith('_top_fix'));
      const desktopTop = desktopRecs.find((r) => r.recommendation_key?.endsWith('_top_fix'));

      // Clear current result and scroll to top first so there is no layout jerk
      // when the new result section appears below the fold.
      setResult(null);
      setImplementationJobs({});
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Small delay so the scroll animation starts before new content paints
      await new Promise((resolve) => setTimeout(resolve, 120));

      setUrl(fullAnalysis.url);
      setResult({
        analysis_id: fullAnalysis.analysis_id,
        url: fullAnalysis.url,
        mobile: {
          score: mobileTop?.score ?? null,
          ai_recommendation: { top_fix: mobileTop || null, next_fixes: mobileRecs.filter((r) => !r.recommendation_key?.endsWith('_top_fix')) },
          core_web_vitals: mobileTop?.core_web_vitals || null,
          pagespeed_metadata: null,
        },
        desktop: {
          score: desktopTop?.score ?? null,
          ai_recommendation: { top_fix: desktopTop || null, next_fixes: desktopRecs.filter((r) => !r.recommendation_key?.endsWith('_top_fix')) },
          core_web_vitals: desktopTop?.core_web_vitals || null,
          pagespeed_metadata: null,
        },
      });
      await fetchImplementationCandidates(fullAnalysis.analysis_id);

      // After results load, scroll to them so user sees the analysis (not the URL input)
      await new Promise((resolve) => setTimeout(resolve, 300));
      const resultsSection = document.querySelector('[data-results-section]');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('Failed to load analysis:', error);
      toast.error('Could not load that analysis. Please run it again.');
    } finally {
      setLoadingHistoryAnalysis(false);
    }
  };

  useEffect(() => {
    if (!supabaseConfigured || !supabaseClient) {
      return undefined;
    }

    let cancelled = false;

    const syncAccountFromSession = async (session, options = {}) => {
      if (cancelled) {
        return;
      }

      if (!session?.access_token) {
        setAccountContext({
          accessToken: null,
          user: null,
          membership: null,
          tenant: null,
          needsOnboarding: false,
          isNewUser: false
        });
        setOnboardingForm((current) => ({
          ...current,
          primaryEmail: ''
        }));
        setAuthReady(true);
        setAuthLoading(false);
        return;
      }

      try {
        const response = options.initialSignIn
          ? await axios.post(
              `${API}/auth/session`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${session.access_token}`
                }
              }
            )
          : await axios.get(`${API}/me`, {
              headers: {
                Authorization: `Bearer ${session.access_token}`
              }
            });

        if (cancelled) {
          return;
        }

        const data = response.data?.data;
        setAccountContext({
          accessToken: session.access_token,
          user: data?.user || null,
          membership: data?.membership || null,
          tenant: data?.tenant || null,
          needsOnboarding: Boolean(data?.needs_onboarding),
          isNewUser: Boolean(data?.is_new_user)
        });
        setOnboardingForm((current) => ({
          workspaceName: current.workspaceName,
          primaryEmail: current.primaryEmail
        }));
        // Unblock the UI as soon as we know who the user is — history loads asynchronously
        setAuthReady(true);
        setAuthLoading(false);
        if (!data?.needs_onboarding) {
          fetchUserHistoryAndUsage(session.access_token);
        }
      } catch (error) {
        console.error('Failed to resolve backend auth session:', error);
        toast.error(error.response?.data?.detail || 'Could not resolve your account session on the backend.');
        if (!cancelled) {
          setAuthReady(true);
          setAuthLoading(false);
        }
      }
    };

    const bootstrapAuth = async () => {
      setAuthLoading(true);
      try {
        const { data, error } = await supabaseClient.auth.getSession();
        if (error) {
          throw error;
        }
        await syncAccountFromSession(data.session, { initialSignIn: false });
      } catch (error) {
        console.error('Failed to restore Supabase session:', error);
        if (!cancelled) {
          setAuthReady(true);
          setAuthLoading(false);
        }
      }
    };

    bootstrapAuth();

    const {
      data: { subscription }
    } = supabaseClient.auth.onAuthStateChange((event, session) => {
      if (cancelled) {
        return;
      }
      if (event === 'SIGNED_OUT') {
        setAccountContext({
          accessToken: null,
          user: null,
          membership: null,
          tenant: null,
          needsOnboarding: false,
          isNewUser: false
        });
        setOnboardingForm({
          workspaceName: '',
          primaryEmail: ''
        });
        setUserHistory({ analyses: [], jobs: [], total: null, allLoaded: false });
        setUserUsage(null);
        setAuthReady(true);
        setAuthLoading(false);
        return;
      }

      // INITIAL_SESSION is already handled by bootstrapAuth() above — skip to avoid duplicate fetches
      if (event === 'INITIAL_SESSION') return;

      setAuthLoading(true);
      void syncAccountFromSession(session, {
        initialSignIn: event === 'SIGNED_IN'
      });
    });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const syncFromInstallCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const installationId = params.get('installation_id');
      const setupAction = params.get('setup_action');
      const githubError = params.get('github_error');

      if (githubError) {
        toast.error('GitHub connection could not be validated for this browser session. Please try connecting again.');
        const cleanedUrl = `${window.location.pathname}${window.location.hash || ''}`;
        window.history.replaceState({}, document.title, cleanedUrl);
        return;
      }

      if (!installationId || !setupAction) {
        return;
      }

      setSyncingInstallation(true);
      try {
        const authHeaders = accountContextRef.current?.accessToken
          ? { Authorization: `Bearer ${accountContextRef.current.accessToken}` }
          : {};
        await axios.post(
          `${API}/github/installations/sync`,
          { installation_id: Number(installationId) },
          { headers: authHeaders }
        );

        const stateResponse = await axios.get(`${API}/github/state`, { headers: authHeaders });
        const stateData = stateResponse.data;

        const repositories = stateData.repositories || [];
        setGithubState((current) => ({
          configured: stateData.configured,
          installUrl: stateData.install_url,
          appSlug: stateData.app_slug,
          connected: stateData.connected,
          repositories,
          selectedRepository: current.selectedRepository && repositories.some((repo) => repo.full_name === current.selectedRepository)
            ? current.selectedRepository
            : (repositories[0]?.full_name || '')
        }));

        toast.success('GitHub installation synced.');
        trackEvent('github_installation_synced', {
          installation_id: installationId,
          setup_action: setupAction
        });
        // Signal other tabs (e.g. the original analysis tab) to refresh GitHub state
        localStorage.setItem('github_install_synced', Date.now().toString());
        localStorage.removeItem('github_install_synced');

        // If opened from another tab (e.g. the main analysis page), close this one
        if (window.opener && !window.opener.closed) {
          window.close();
          return;
        }
      } catch (error) {
        console.error('Failed to sync GitHub installation:', error);
        toast.error(error.response?.data?.detail || 'Failed to sync GitHub installation.');
      } finally {
        const cleanedUrl = `${window.location.pathname}${window.location.hash || ''}`;
        window.history.replaceState({}, document.title, cleanedUrl);
        setSyncingInstallation(false);
      }
    };

    if (!authReady || installSyncAttemptedRef.current) {
      return;
    }
    installSyncAttemptedRef.current = true;
    syncFromInstallCallback();
  }, [authReady]);


  useEffect(() => {
    // SSE: real-time repo updates when GitHub webhook fires (production)
    const es = new EventSource(`${API}/github/events`, { withCredentials: true });
    es.onmessage = (e) => {
      if (e.data === 'repos_updated' && githubStateLoadedRef.current) {
        const token = accountContextRef.current?.accessToken || null;
        refreshGitHubStateRef.current?.(token);
      }
    };

    // Visibility fallback: covers localhost (no inbound webhooks) and any SSE gap
    const onVisible = () => {
      if (document.visibilityState === 'visible' && githubStateLoadedRef.current) {
        const token = accountContextRef.current?.accessToken || null;
        refreshGitHubStateRef.current?.(token);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      es.close();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const refreshGitHubState = async (accessToken = null) => {
    const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    try {
      const response = await axios.get(`${API}/github/state`, { headers: authHeaders });
      const data = response.data;
      const repositories = data.repositories || [];
      setGithubConfigStatus(data.config_status ?? null);
      setGithubState((current) => ({
        configured: data.configured,
        installUrl: data.install_url,
        appSlug: data.app_slug,
        connected: data.connected,
        repositories,
        selectedRepository: current.selectedRepository && repositories.some((repo) => repo.full_name === current.selectedRepository)
          ? current.selectedRepository
          : (repositories[0]?.full_name || '')
      }));
      githubStateLoadedRef.current = true;
      return true;
    } catch (error) {
      console.error('Failed to load GitHub integration state:', error);
      setGithubConfigStatus(null);
      return false;
    }
  };
  refreshGitHubStateRef.current = refreshGitHubState;

  const fetchImplementationCandidates = async (analysisId) => {
    if (!analysisId) {
      setImplementationCandidates({});
      return;
    }

    setImplementationLoading(true);
    try {
      const headers = accountContext.accessToken
        ? { Authorization: `Bearer ${accountContext.accessToken}` }
        : {};
      const response = await axios.post(
        `${API}/implementation/candidates`,
        { analysis_id: analysisId },
        { headers, timeout: 15000 }
      );

      const candidateMap = {};
      response.data.candidates.forEach((candidate) => {
        candidateMap[candidate.recommendation_key] = candidate;
      });
      setImplementationCandidates(candidateMap);
    } catch (error) {
      console.error('Failed to load implementation candidates:', error);
      toast.error('Unable to load implementation candidates right now.');
      setImplementationCandidates({});
    } finally {
      setImplementationLoading(false);
    }
  };

  const getRecommendationKey = (strategy, type, index = null) => {
    if (type === 'top_fix') {
      return `${strategy}_top_fix`;
    }

    return `${strategy}_next_fix_${index}`;
  };

  const getImplementationCandidate = (strategy, type, index = null) => {
    const recommendationKey = getRecommendationKey(strategy, type, index);
    return implementationCandidates[recommendationKey] || null;
  };

  const isTerminalImplementationStatus = (status) => (
    ['completed', 'skipped', 'failed'].includes(status)
  );

  const trackImplementationTerminalState = (jobData, recommendationKey) => {
    if (!jobData?.job_id || !isTerminalImplementationStatus(jobData.status)) {
      return;
    }

    const eventKey = `${jobData.job_id}:${jobData.status}`;
    if (trackedImplementationEventsRef.current.has(eventKey)) {
      return;
    }

    trackedImplementationEventsRef.current.add(eventKey);
    const candidate = implementationCandidates[recommendationKey];
    const payload = {
      recommendation_key: recommendationKey,
      repository_full_name: jobData.repository_full_name,
      target_branch: jobData.target_branch,
      fix_type: candidate?.fix_type || 'unknown',
      policy_class: jobData.policy_class || candidate?.policy_class || 'unknown',
      changed_files_count: jobData.changed_files?.length || 0,
      planned_files_count: jobData.planned_files?.length || 0,
      excluded_files_count: jobData.excluded_files?.length || 0,
      validation_results_count: jobData.validation_results?.length || 0,
      has_pr_url: jobData.pr_url ? 'yes' : 'no'
    };

    if (jobData.status === 'completed') {
      trackImplementationJobCompleted(payload);
      if (jobData.pr_url) {
        trackDraftPrOpened({
          ...payload,
          pr_url: jobData.pr_url
        });
      }
      return;
    }

    if (jobData.status === 'skipped') {
      trackImplementationJobSkipped({
        ...payload,
        reason_count: jobData.reasons?.length || 0
      });
      return;
    }

    trackImplementationJobFailed({
      ...payload,
      reason_count: jobData.reasons?.length || 0
    });
  };

  const getSelectedRepository = () => (
    githubState.repositories.find((repo) => repo.full_name === githubState.selectedRepository) || null
  );

  const handleConnectGitHub = async () => {
    trackGitHubConnectStarted('implementation_panel');
    trackEvent('github_connect_clicked', {
      app_slug: githubState.appSlug || 'not_configured'
    });

    setGithubConnecting(true);
    try {
      const stateResponse = await axios.get(`${API}/github/state`);
      const installUrl = stateResponse.data.install_url;

      setGithubState((current) => ({
        ...current,
        configured: stateResponse.data.configured,
        installUrl,
        appSlug: stateResponse.data.app_slug
      }));

      if (installUrl) {
        window.open(installUrl, '_blank');
        return;
      }
    } catch (error) {
      console.error('Failed to prepare GitHub App install URL:', error);
      toast.error('Could not prepare GitHub connection. Please try again.');
    } finally {
      setGithubConnecting(false);
    }

    toast.error('GitHub App install URL is not configured yet.');
  };

  const handleRefreshRepositories = async () => {
    setSyncingInstallation(true);
    try {
      const refreshed = await refreshGitHubState(accountContext.accessToken);
      if (!refreshed) {
        throw new Error('refresh_failed');
      }
      toast.success('GitHub repositories refreshed.');
    } catch (error) {
      console.error('Failed to refresh GitHub repositories:', error);
      toast.error('Failed to refresh GitHub repositories.');
    } finally {
      setSyncingInstallation(false);
    }
  };

  const handleManualInstallationSync = async () => {
    const parsedInstallationId = Number(manualInstallationId);
    if (!parsedInstallationId || Number.isNaN(parsedInstallationId)) {
      toast.error('Enter a valid numeric GitHub installation ID.');
      return;
    }

    setSyncingInstallation(true);
    try {
      const syncAuthHeaders = accountContext.accessToken ? { Authorization: `Bearer ${accountContext.accessToken}` } : {};
      await axios.post(`${API}/github/installations/sync`, {
        installation_id: parsedInstallationId
      }, { headers: syncAuthHeaders });

      const refreshed = await refreshGitHubState(accountContext.accessToken);
      if (!refreshed) {
        throw new Error('refresh_failed');
      }

      trackEvent('github_installation_synced_manual', {
        installation_id: parsedInstallationId
      });
      toast.success('GitHub installation synced.');
    } catch (error) {
      console.error('Failed to manually sync GitHub installation:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync installation.');
    } finally {
      setSyncingInstallation(false);
    }
  };

  const handleRepositoryChange = (repositoryFullName) => {
    setGithubState((current) => ({
      ...current,
      selectedRepository: repositoryFullName
    }));
  };

  const handleClearSelectedRepository = () => {
    setGithubState((current) => ({
      ...current,
      selectedRepository: ''
    }));
  };

  const handleRemoveRepository = async (repositoryFullName) => {
    const authHeaders = accountContext.accessToken ? { Authorization: `Bearer ${accountContext.accessToken}` } : {};
    try {
      await axios.delete(`${API}/github/repositories/${encodeURIComponent(repositoryFullName)}`, { headers: authHeaders });
      const refreshed = await refreshGitHubState(accountContext.accessToken);
      if (!refreshed) {
        throw new Error('refresh_failed');
      }
      toast.success(`Removed ${repositoryFullName} from the connected repository list.`);
    } catch (error) {
      console.error('Failed to remove GitHub repository:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove repository.');
    }
  };

  const pollImplementationJob = async (jobId, recommendationKey) => {
    try {
      const response = await axios.get(`${API}/implementation/jobs/${jobId}`);
      setImplementationJobs((current) => ({
        ...current,
        [recommendationKey]: response.data
      }));
      trackImplementationTerminalState(response.data, recommendationKey);

      if (!isTerminalImplementationStatus(response.data.status)) {
        window.setTimeout(() => {
          pollImplementationJob(jobId, recommendationKey);
        }, 1200);
      }
    } catch (error) {
      console.error('Failed to poll implementation job:', error);
    }
  };

  const homeFaqs = [
    {
      question: 'What does this website speed test check?',
      answer: 'The tool checks Lighthouse-based performance signals for mobile and desktop, including Core Web Vitals metrics, rendering bottlenecks, and prioritised fixes you can use to improve page speed.'
    },
    {
      question: 'Is this a Core Web Vitals checker?',
      answer: 'Yes. It helps you review Core Web Vitals performance issues and translates them into practical next steps. The most important metrics to watch are LCP, INP, and CLS.'
    },
    {
      question: 'Can I test mobile and desktop performance separately?',
      answer: 'Yes. Each analysis returns separate mobile and desktop results so you can compare how the same page performs under different conditions and prioritise the biggest gaps first.'
    },
    {
      question: 'Can this tool generate code or a draft PR?',
      answer: 'Yes. Web Speed Advisor can generate implementation snippets for recommendations, and the current implementation beta can prepare draft GitHub pull requests for a narrow set of low-risk frontend fixes.'
    },
    {
      question: 'Who is this tool for?',
      answer: 'It is useful for site owners, marketers, agencies, developers, and SEO teams that need a faster way to diagnose why a page is slow and what to fix next.'
    },
    {
      question: 'Is the website speed test free to use?',
      answer: 'Yes. The core speed test and Core Web Vitals checker are free with no sign-up required. AI-generated code snippets are available on the free tier during the current beta period. Free accounts include 10 analyses and 3 draft PRs per month. Usage resets on the 1st of each calendar month.'
    },
    {
      question: 'How does the monthly usage limit work?',
      answer: 'Usage is counted per calendar month for your account. Free accounts get 10 analyses and 3 draft PRs each month. The counter resets to zero on the 1st of every month, so your full allowance is restored at the start of each new month. You can see your current usage in the account panel after signing in.'
    },
    {
      question: 'How does the AI speed optimisation work?',
      answer: 'After the Lighthouse analysis completes, an AI model reads your page speed results and site context to generate prioritised, plain-English recommendations. It explains what to fix, where to apply the change, and why it will improve performance.'
    }
  ];

  const normalizeUrl = (url) => {
    // Trim whitespace
    url = url.trim();
    
    // If URL doesn't have a protocol, add https://
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    
    return url;
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isPrivateUrl = (url) => {
    try {
      const { hostname } = new URL(url);
      if (/^(localhost|.*\.local)$/i.test(hostname)) return true;
      if (/^127\.\d+\.\d+\.\d+$/.test(hostname)) return true;
      if (/^10\.\d+\.\d+\.\d+$/.test(hostname)) return true;
      if (/^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(hostname)) return true;
      if (/^192\.168\.\d+\.\d+$/.test(hostname)) return true;
      if (/^169\.254\.\d+\.\d+$/.test(hostname)) return true;
      if (/^0\.0\.0\.0$/.test(hostname)) return true;
      if (hostname === '::1') return true;
      return false;
    } catch {
      return false;
    }
  };

  const extractHostname = (value) => {
    if (!value) {
      return '';
    }

    try {
      return new URL(normalizeUrl(value)).hostname.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  };

  const tokenizeForMatch = (value) => (
    (value || '')
      .toLowerCase()
      .replace(/https?:\/\//g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .map((token) => token.trim())
      .filter((token) => token.length >= 3 && !['www', 'com', 'net', 'org', 'app', 'site', 'www2'].includes(token))
  );

  const getRepositoryMatch = (repository, websiteUrl) => {
    if (!repository || !websiteUrl) {
      return null;
    }

    const websiteHost = extractHostname(websiteUrl);
    if (!websiteHost) {
      return null;
    }

    const homepageHost = extractHostname(repository.homepage_url || '');
    const repoCorpus = [
      repository.full_name,
      repository.name,
      repository.description,
      repository.homepage_url
    ].filter(Boolean).join(' ').toLowerCase();

    const websiteTokens = Array.from(new Set(tokenizeForMatch(websiteHost)));
    const matchingTokens = websiteTokens.filter((token) => repoCorpus.includes(token));

    let score = 0.1;
    const reasons = [];

    if (homepageHost && homepageHost === websiteHost) {
      score = 0.98;
      reasons.push('Repository homepage exactly matches the analyzed website domain.');
    } else if (homepageHost && (homepageHost.endsWith(websiteHost) || websiteHost.endsWith(homepageHost))) {
      score = 0.9;
      reasons.push('Repository homepage is closely aligned with the analyzed website domain.');
    } else if (matchingTokens.length > 0) {
      score = Math.min(0.82, 0.45 + (matchingTokens.length * 0.12));
      reasons.push(`Repository metadata overlaps with website tokens: ${matchingTokens.slice(0, 4).join(', ')}.`);
    } else {
      reasons.push('No strong domain or naming overlap was found between the website and selected repository.');
    }

    if (repository.owner_login && websiteTokens.includes(repository.owner_login.toLowerCase())) {
      score = Math.max(score, 0.72);
      reasons.push('Repository owner name overlaps with the website name.');
    }

    const label = score >= 0.9 ? 'Likely Match' : score >= 0.6 ? 'Possible Match' : 'Weak Match';
    return {
      score,
      label,
      reasons: Array.from(new Set(reasons))
    };
  };

  const formatFetchTime = (value) => {
    if (!value) return 'Unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getPageSpeedSourceLabel = (metadata) => {
    if (!metadata) {
      return 'Unknown source';
    }

    if (metadata.cached) {
      const age = metadata.cache_age_seconds ?? 0;
      if (age < 60) {
        return 'Cached just now';
      }
      const minutes = Math.round(age / 60);
      return `Cached ${minutes} min ago`;
    }

    return 'Fresh live run';
  };

  const handleUrlChange = (value) => {
    setUrl(value);
    setDetectedTech(null);
    setTechDetectionMessage('');
    setSiteContext({ siteType: null, techStack: null });
    setImplementationCandidates({});
    setImplementationJobs({});
    setImplementationLimitReached(false);
    setLeadEmail('');
    setLeadNote('');
    setLeadCaptureSubmitted(false);
    setAnalysisError(null);
  };

  const handleAnalyzeClick = async () => {
    if (!BACKEND_URL) {
      setAnalysisError({
        message: 'The frontend is missing its backend API configuration. Set `REACT_APP_BACKEND_URL` and try again.',
        type: 'backend_unavailable'
      });
      return;
    }

    if (!url.trim()) {
      setAnalysisError({ message: 'Please enter a URL to analyse.', type: 'validation' });
      return;
    }

    const normalizedUrl = normalizeUrl(url);

    if (!validateUrl(normalizedUrl)) {
      setAnalysisError({ message: 'Please enter a valid URL — for example, example.com or www.example.com.', type: 'validation' });
      return;
    }

    if (isPrivateUrl(normalizedUrl)) {
      setAnalysisError({ message: 'Private and local URLs cannot be analysed. Please enter a publicly accessible website URL.', type: 'validation' });
      return;
    }

    setAnalysisError(null);
    setUrl(normalizedUrl);

    // Abort any in-flight analysis
    if (analysisAbortRef.current) analysisAbortRef.current.abort();
    const abortController = new AbortController();
    analysisAbortRef.current = abortController;

    // Show modal immediately — don't wait for tech detection
    setDetecting(true);
    setDetectedTech(null);
    setTechDetectionMessage('');
    setShowContextModal(true);

    // Create a promise that resolves when the user submits (or auto-dismisses) the modal
    let resolveContext;
    const contextPromise = new Promise((resolve) => { resolveContext = resolve; });
    pendingContextRef.current = resolveContext;

    // Fire tech detection in background — update modal if still open
    axios.post(`${API}/detect-tech`, { url: normalizedUrl }, { timeout: 5000 })
      .then((detectResponse) => {
        const detectedStack = detectResponse.data?.tech_stack || null;
        setDetectedTech(detectedStack);
        setTechDetectionMessage(
          detectedStack
            ? `Detected ${detectedStack}. Change it if needed.`
            : 'Auto-detection could not identify the stack. You can still choose it manually.'
        );
      })
      .catch(() => {
        setTechDetectionMessage('Auto-detection is unavailable right now. You can still choose the stack manually or skip this step.');
      })
      .finally(() => setDetecting(false));

    // Fire analysis stream immediately in parallel
    runAnalysis({ siteType: null, techStack: null }, false, abortController.signal, contextPromise, normalizedUrl);
  };

  const handleContextSubmit = async (context) => {
    setShowContextModal(false);
    setSiteContext(context);
    // Resolve the pending context so the in-flight stream can pick up tech/siteType
    if (pendingContextRef.current) {
      pendingContextRef.current(context);
      pendingContextRef.current = null;
    }
  };

  const getAnalysisErrorState = (error) => {
    const status = error.response?.status;
    const detail = typeof error.response?.data?.detail === 'string'
      ? error.response.data.detail.trim()
      : '';

    if (!error.response) {
      return {
        message: 'Unable to reach the analysis service. Please check your connection and try again.',
        type: 'backend_unavailable'
      };
    }

    if (status === 503 || /temporarily unavailable|temporarily busy|unavailable after retries/i.test(detail)) {
      return {
        message: 'Google PageSpeed API is temporarily busy. Please wait a moment and try again.',
        type: 'api_busy'
      };
    }

    if (status === 504 || /timed out/i.test(detail)) {
      return {
        message: 'The PageSpeed API took too long to respond. Please try again. If the issue persists, the target site may be slow or temporarily unreachable.',
        type: 'timeout'
      };
    }

    if (status === 429) {
      return {
        message: detail || 'Rate limit exceeded. Please wait a few minutes before trying again.',
        type: 'rate_limit'
      };
    }

    if (status === 400 && detail) {
      return {
        message: detail,
        type: 'validation'
      };
    }

    return {
      message: 'Failed to analyse the URL. Please try again.',
      type: status ? 'api_error' : 'unknown'
    };
  };

  const runAnalysis = async (context, forceRefresh = false, abortSignal = null, contextPromise = null, urlOverride = null) => {
    const startTime = Date.now();
    let phase1Time = null;

    setLoading(true);
    setLoadingRecommendations(false);
    setResult(null);
    setAnalysisError(null);
    setProcessingTime(null);
    setImplementationCandidates({});
    setImplementationJobs({});
    setImplementationLimitReached(false);
    setLeadEmail('');
    setLeadNote('');
    setLeadCaptureSubmitted(false);

    setTimeout(() => {
      const progressSection = document.querySelector('[data-progress-section]');
      if (progressSection) progressSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    // Wait for context from modal, but if API returns pagespeed event first, skip the wait
    // We use a race: contextPromise vs a sentinel that resolves immediately if no contextPromise
    let resolvedContext = context;
    if (contextPromise) {
      // We'll update resolvedContext once we get pagespeed event — contextPromise races internally
      contextPromise.then((ctx) => {
        resolvedContext = ctx;
        setSiteContext(ctx);
      });
    }

    const effectiveUrl = urlOverride || url;
    trackAnalysisStart(effectiveUrl, context.siteType, context.techStack);

    const params = new URLSearchParams({ url: effectiveUrl, force_refresh: forceRefresh });
    if (context.siteType) params.set('site_type', context.siteType);
    if (context.techStack) params.set('tech_stack', context.techStack);
    const streamUrl = `${API}/analyze/stream?${params.toString()}`;

    try {
      const fetchRes = await fetch(streamUrl, {
        credentials: 'include',
        signal: abortSignal,
        headers: accountContext.accessToken
          ? { Authorization: `Bearer ${accountContext.accessToken}` }
          : {},
      });

      if (!fetchRes.ok) {
        const errData = await fetchRes.json().catch(() => ({}));
        const fakeError = { response: { status: fetchRes.status, data: errData } };
        throw fakeError;
      }

      const reader = fetchRes.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completeData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop(); // keep incomplete chunk

        for (const part of parts) {
          const eventMatch = part.match(/^event: (\w+)/m);
          const dataMatch = part.match(/^data: (.+)/ms);
          if (!eventMatch || !dataMatch) continue;

          const eventType = eventMatch[1];
          let payload;
          try { payload = JSON.parse(dataMatch[1]); } catch { continue; }

          if (eventType === 'error') {
            const fakeError = { response: { status: payload.status, data: { detail: payload.detail } } };
            throw fakeError;
          }

          if (eventType === 'pagespeed') {
            // Phase 1 arrived — auto-dismiss modal if still open (tech detection lost the race)
            phase1Time = Date.now() - startTime;
            setShowContextModal(false);
            if (pendingContextRef.current) {
              pendingContextRef.current({ siteType: null, techStack: null });
              pendingContextRef.current = null;
            }
            setResult({
              analysis_id: null,
              url,
              mobile: { score: payload.mobile.score, core_web_vitals: payload.mobile.core_web_vitals, pagespeed_metadata: payload.mobile.pagespeed_metadata, ai_recommendation: null },
              desktop: { score: payload.desktop.score, core_web_vitals: payload.desktop.core_web_vitals, pagespeed_metadata: payload.desktop.pagespeed_metadata, ai_recommendation: null },
            });
            setLoadingRecommendations(true);
            setLoading(false);
            setTimeout(() => {
              const resultsSection = document.querySelector('[data-results-section]');
              if (resultsSection) resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 150);
          }

          if (eventType === 'complete') {
            completeData = payload;
            const totalMs = Date.now() - startTime;
            const timing = { phase1Ms: phase1Time, totalMs };
            setProcessingTime(timing);
            try { sessionStorage.setItem('lastAnalysisTiming', JSON.stringify({ ...timing, url })); } catch {}
            setResult(payload);
            setLoadingRecommendations(false);
          }
        }
      }

      if (!completeData) throw new Error('Stream ended without a complete event');

      const d = completeData;
      refreshGitHubState(accountContext.accessToken).catch(() => {});
      await fetchImplementationCandidates(d.analysis_id);

      if (accountContext.accessToken) {
        const buildRecs = (stratKey) => {
          const strat = d[stratKey];
          if (!strat?.ai_recommendation) return [];
          const { top_fix, next_fixes = [] } = strat.ai_recommendation;
          const base = { strategy: stratKey, score: strat.score, core_web_vitals: strat.core_web_vitals || [] };
          return [
            ...(top_fix ? [{ ...top_fix, ...base, recommendation_key: `${stratKey}_top_fix` }] : []),
            ...next_fixes.map((fix, i) => ({ ...fix, ...base, recommendation_key: `${stratKey}_fix_${i}` })),
          ];
        };
        setUserHistory((prev) => {
          const isNew = !prev.analyses.some((a) => a.analysis_id === d.analysis_id);
          const newAnalyses = [{
            analysis_id: d.analysis_id, url: d.url, created_at: new Date().toISOString(),
            recommendations: [...buildRecs('mobile'), ...buildRecs('desktop')],
          }, ...prev.analyses.filter((a) => a.analysis_id !== d.analysis_id)];
          const newTotal = isNew && prev.total !== null ? prev.total + 1 : prev.total;
          return { ...prev, analyses: newAnalyses, total: newTotal };
        });
        axios.get(`${API}/user/usage`, { headers: { Authorization: `Bearer ${accountContext.accessToken}` } })
          .then((res) => setUserUsage(res.data)).catch(() => {});
      }

      toast.success('Analysis complete!');
      trackAnalysisComplete(url, d.mobile.score, d.desktop.score, context.siteType, context.techStack, true);

    } catch (error) {
      console.error('Error analysing URL:', error);
      const { message: errorMessage, type: errorType } = getAnalysisErrorState(error);
      trackAnalysisError(url, errorType);
      setAnalysisError({ message: errorMessage, type: errorType });
      setShowContextModal(false);
      if (pendingContextRef.current) {
        pendingContextRef.current({ siteType: null, techStack: null });
        pendingContextRef.current = null;
      }
    } finally {
      setLoading(false);
      setLoadingRecommendations(false);
    }
  };

  const handleRefreshLiveResult = async () => {
    if (!url || !siteContext) {
      toast.error('Run an analysis first.');
      return;
    }

    setRefreshingLiveData(true);
    try {
      await runAnalysis(siteContext, true);
      toast.success('Fetched a fresh live PageSpeed result.');
    } finally {
      setRefreshingLiveData(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!supabaseConfigured || !supabaseClient) {
      toast.error('Supabase auth is not configured in the frontend.');
      return;
    }

    setAuthLoading(true);
    try {
      const { data, error } = await supabaseClient.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          skipBrowserRedirect: true
        }
      });
      if (error) {
        throw error;
      }
      if (!data?.url) {
        throw new Error('Supabase did not return an OAuth redirect URL.');
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error('Failed to start Google sign-in:', error);
      toast.error(error.message || 'Could not start Google sign-in.');
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (!supabaseClient) {
      return;
    }

    try {
      const { error } = await supabaseClient.auth.signOut();
      if (error) {
        throw error;
      }
      toast.success('Signed out.');
    } catch (error) {
      console.error('Failed to sign out:', error);
      toast.error(error.message || 'Could not sign out right now.');
    }
  };

  const handleCreateTenantOnboarding = async (event) => {
    event.preventDefault();

    const workspaceName = onboardingForm.workspaceName.trim();
    const primaryEmail = onboardingForm.primaryEmail.trim();
    if (!workspaceName) {
      toast.error('Enter a workspace name.');
      return;
    }

    if (!primaryEmail) {
      toast.error('Enter a primary email.');
      return;
    }

    if (!accountContext.accessToken) {
      toast.error('Sign in again before creating a workspace.');
      return;
    }

    setOnboardingSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/create-tenant`,
        {
          workspace_name: workspaceName,
          primary_email: primaryEmail
        },
        {
          headers: {
            Authorization: `Bearer ${accountContext.accessToken}`
          }
        }
      );

      const data = response.data?.data;
      setAccountContext((current) => ({
        ...current,
        user: data?.user || current.user,
        membership: data?.membership || null,
        tenant: data?.tenant || null,
        needsOnboarding: Boolean(data?.needs_onboarding),
        isNewUser: Boolean(data?.is_new_user)
      }));
      setOnboardingForm((current) => ({
        ...current,
        primaryEmail
      }));
      if (!data?.needs_onboarding && accountContext.accessToken) {
        fetchUserHistoryAndUsage(accountContext.accessToken);
      }
      toast.success('Workspace created.');
    } catch (error) {
      console.error('Failed to create tenant workspace:', error);
      toast.error(error.response?.data?.detail || 'Could not create the workspace right now.');
    } finally {
      setOnboardingSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleAnalyzeClick();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackTabSwitch(activeTab, tab);
  };

  const handleGenerateSnippet = async (fix, options = {}) => {
    const contentKind = options.guidanceMode === 'review_guide'
      ? 'review_guide'
      : options.guidanceMode === 'manual_guide'
        ? 'manual_guide'
        : 'snippet';
    const implementationCandidate = options.implementationCandidate || null;
    setLoadingSnippetKind(contentKind);
    setLoadingSnippet(true);
    
    // Track snippet generation with persistent tracking
    trackSnippetGenerated(fix.title, siteContext.techStack, activeTab, contentKind);

    try {
      const response = await axios.post(`${API}/generate-snippet`, {
        fix_title: fix.title,
        fix_details: fix.technical_details,
        fix_steps: fix.how_to_fix_steps,
        tech_stack: siteContext.techStack,
        evidence: fix.evidence,
        site_type: siteContext.siteType,
        strategy: activeTab,
        guidance_mode: options.guidanceMode || 'snippet',
        policy_class: implementationCandidate?.policy_class || null,
        fix_type: implementationCandidate?.fix_type || null,
        validation_profile: implementationCandidate?.validation_profile || null,
        recommendation_reasons: implementationCandidate?.reasons || []
      });

      setSnippetModal({
        isOpen: true,
        snippet: response.data.snippet,
        fixTitle: fix.title,
        techStack: response.data.tech_stack,
        contentKind: response.data.content_kind || contentKind
      });
    } catch (error) {
      console.error('Error generating snippet:', error);
      toast.error(
        contentKind === 'review_guide'
          ? 'Failed to generate the review guide. Please try again.'
          : contentKind === 'manual_guide'
            ? 'Failed to generate the manual guide. Please try again.'
          : 'Failed to generate snippet. Please try again.'
      );
    } finally {
      setLoadingSnippet(false);
      setLoadingSnippetKind('snippet');
    }
  };

  const handleCreateImplementationJob = async (strategy, type, index = null) => {
    const recommendationKey = getRecommendationKey(strategy, type, index);
    const candidate = implementationCandidates[recommendationKey];
    trackImplementationCtaClicked({
      recommendation_key: recommendationKey,
      fix_type: candidate?.fix_type || 'unknown',
      strategy,
      github_connected: githubState.connected ? 'yes' : 'no',
      repository_selected: githubState.selectedRepository ? 'yes' : 'no'
    });

    if (!candidate?.eligible) {
      toast.error('This recommendation is not eligible for automated implementation in the current MVP.');
      return;
    }

    if (!candidate?.end_to_end_supported) {
      toast.info('This recommendation is advisory-only right now. Draft PR generation is currently available only for selected low-risk fix classes.');
      return;
    }

    if (!githubState.connected) {
      toast.error('Connect GitHub before generating a draft PR.');
      return;
    }

    if (!githubState.selectedRepository) {
      toast.error('Select a repository before generating a draft PR.');
      return;
    }

    if (!result?.analysis_id) {
      toast.error('Analysis context is missing. Please run the analysis again.');
      return;
    }

    setCreatingImplementationKey(recommendationKey);
    trackEvent('implementation_job_requested', {
      recommendation_key: recommendationKey,
      fix_type: candidate.fix_type || 'unknown',
      strategy,
      repository_full_name: githubState.selectedRepository
    });

    const jobAuthHeaders = accountContext.accessToken
      ? { Authorization: `Bearer ${accountContext.accessToken}` }
      : {};

    try {
      const response = await axios.post(
        `${API}/implementation/jobs`,
        {
          analysis_id: result.analysis_id,
          recommendation_key: recommendationKey,
          repository_full_name: githubState.selectedRepository,
          target_branch: getSelectedRepository()?.default_branch || 'main'
        },
        { headers: jobAuthHeaders }
      );

      setImplementationJobs((current) => ({
        ...current,
        [recommendationKey]: response.data
      }));
      trackImplementationJobCreated({
        recommendation_key: recommendationKey,
        repository_full_name: response.data.repository_full_name,
        target_branch: response.data.target_branch,
        fix_type: candidate.fix_type || 'unknown',
        policy_class: response.data.policy_class || candidate.policy_class || 'unknown'
      });

      pollImplementationJob(response.data.job_id, recommendationKey);
      toast.success('Implementation job started.');
    } catch (error) {
      console.error('Failed to create implementation job:', error);
      if (error.response?.status === 403) {
        setImplementationLimitReached(true);
        trackEvent('implementation_limit_reached', {
          recommendation_key: recommendationKey,
          fix_type: candidate.fix_type || 'unknown',
          strategy,
          repository_full_name: githubState.selectedRepository
        });
      }
      toast.error(error.response?.data?.detail || 'Failed to create implementation job.');
    } finally {
      setCreatingImplementationKey(null);
    }
  };

  const latestImplementationOutcomeJob = Object.values(implementationJobs)
    .filter((job) => isTerminalImplementationStatus(job.status))
    .sort((left, right) => new Date(right.created_at || 0) - new Date(left.created_at || 0))[0] || null;

  const leadCaptureConfig = latestImplementationOutcomeJob
    ? latestImplementationOutcomeJob.status === 'completed'
      ? {
          title: 'Want help fixing the next issues too?',
          description: 'The safe draft PR worked for one bounded fix. If you want broader implementation help across the rest of the report, leave your email and a short note.',
          context: 'successful_draft_pr'
        }
      : {
          title: 'Need us to handle the harder fixes?',
          description: 'This recommendation stayed inside the safe beta boundary and did not turn into a merge-ready change. Leave your email if you want hands-on help with the remaining work.',
          context: latestImplementationOutcomeJob.status === 'skipped' ? 'skipped_fix' : 'failed_fix'
        }
    : implementationLimitReached
      ? {
          title: 'Need more than one draft PR in this beta?',
          description: 'The free implementation beta currently allows one successful draft PR per session. Leave your email if you want broader help beyond the bounded safe beta flow.',
          context: 'session_limit'
        }
    : null;

  const handleLeadCaptureSubmit = async (event) => {
    event.preventDefault();

    if (!leadEmail.trim()) {
      toast.error('Enter an email address so we can follow up.');
      return;
    }

    if (!leadCaptureConfig) {
      return;
    }

    setLeadCaptureSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        email: leadEmail.trim(),
        note: leadNote.trim(),
        source: 'implementation_beta',
        website_url: result?.url,
        repository_full_name: latestImplementationOutcomeJob?.repository_full_name,
        context: leadCaptureConfig.context
      });
      setLeadCaptureSubmitted(true);
      trackEvent('lead_capture_submitted', {
        source: 'implementation_beta',
        context: leadCaptureConfig.context,
        repository_full_name: latestImplementationOutcomeJob?.repository_full_name || 'unknown',
        outcome_status: latestImplementationOutcomeJob?.status || 'unknown'
      });
      toast.success('Thanks. We will follow up.');
    } catch (error) {
      console.error('Lead capture failed:', error);
      toast.error(error.response?.data?.detail || 'Could not submit your request right now.');
    } finally {
      setLeadCaptureSubmitting(false);
    }
  };

  const currentData = result ? result[activeTab] : null;
  const currentPageSpeedMetadata = currentData?.pagespeed_metadata || null;
  const nextFixEntries = currentData?.ai_recommendation?.next_fixes
    ? currentData.ai_recommendation.next_fixes.map((fix, index) => ({ fix, index }))
    : [];
  const nextInsightEntries = nextFixEntries.filter(({ fix }) => fix.audit_source_group !== 'diagnostic');
  const diagnosticEntries = nextFixEntries.filter(({ fix }) => fix.audit_source_group === 'diagnostic');
  const selectedRepository = getSelectedRepository();
  const selectedRepositoryMatch = getRepositoryMatch(selectedRepository, result?.url);
  const bestRepositoryMatch = result?.url
    ? githubState.repositories
        .map((repo) => ({
          repository: repo,
          match: getRepositoryMatch(repo, result.url)
        }))
        .filter((entry) => entry.match)
        .sort((left, right) => right.match.score - left.match.score)[0] || null
    : null;
  const isLocalGitHubTestMode = BACKEND_URL?.includes('127.0.0.1') || BACKEND_URL?.includes('localhost') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const renderLocalGitHubTestPanel = () => (
    <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-950 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
            Developer Tools
          </p>
          <p className="text-sm text-slate-300 max-w-2xl">
            Use this only for localhost testing. It is meant for manual installation sync and backend readiness checks, not day-to-day repository selection.
          </p>
          {githubConfigStatus && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`rounded-full px-3 py-1 font-semibold ${githubConfigStatus.installation_auth_ready ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                Install Auth {githubConfigStatus.installation_auth_ready ? 'Ready' : 'Missing'}
              </span>
              <span className={`rounded-full px-3 py-1 font-semibold ${githubConfigStatus.webhook_ready ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                Webhook {githubConfigStatus.webhook_ready ? 'Ready' : 'Missing'}
              </span>
              <span className={`rounded-full px-3 py-1 font-semibold ${githubConfigStatus.oauth_client_id_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                OAuth Client ID {githubConfigStatus.oauth_client_id_configured ? 'Set' : 'Missing'}
              </span>
              <span className={`rounded-full px-3 py-1 font-semibold ${githubState.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                Repo Sync {githubState.connected ? 'Connected' : 'Not Synced'}
              </span>
            </div>
          )}
          {githubState.selectedRepository && (
            <p className="text-xs text-slate-400">
              Active repository: {githubState.selectedRepository}
            </p>
          )}
        </div>

        <div className="flex w-full max-w-md flex-col gap-3">
          <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
            Manual Installation ID
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualInstallationId}
              onChange={(e) => setManualInstallationId(e.target.value)}
              placeholder="e.g. 12345678"
              className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-white placeholder:text-slate-500"
            />
            <button
              onClick={handleManualInstallationSync}
              className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={syncingInstallation}
            >
              {syncingInstallation ? 'Syncing…' : 'Sync'}
            </button>
          </div>
          <button
            onClick={handleRefreshRepositories}
            className="rounded-xl border border-slate-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
          >
            {syncingInstallation ? 'Refreshing…' : 'Refresh GitHub Repositories'}
          </button>
          {githubState.configured && (
            <button
              onClick={handleConnectGitHub}
              disabled={githubConnecting}
              className="rounded-xl border border-blue-700 px-3 py-2 text-sm font-semibold text-blue-200 transition-colors hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {githubConnecting ? 'Opening GitHub…' : 'Attach Another Repository'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderRepositoryManager = ({ dark = false, compact = false } = {}) => {
    if (githubState.repositories.length === 0) {
      return null;
    }

    const shellClass = dark
      ? 'rounded-xl border border-slate-700 bg-slate-950/70 p-4'
      : 'rounded-xl border border-slate-200 bg-white p-4';
    const titleClass = dark
      ? 'text-xs font-semibold uppercase tracking-[0.14em] text-slate-300'
      : 'text-xs font-semibold uppercase tracking-[0.14em] text-slate-600';
    const textClass = dark ? 'text-slate-200' : 'text-slate-800';
    const mutedClass = dark ? 'text-slate-400' : 'text-slate-500';
    const itemClass = dark
      ? 'rounded-xl border border-slate-700 bg-slate-900 p-3'
      : 'rounded-xl border border-slate-200 bg-slate-50 p-3';
    const secondaryButtonClass = dark
      ? 'rounded-lg border border-slate-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800'
      : 'rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100';
    const dangerButtonClass = dark
      ? 'rounded-lg border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-200 transition-colors hover:bg-red-500/10'
      : 'rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50';

    return (
      <div className={shellClass}>
        <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className={titleClass}>Repository Manager</p>
            <p className={`mt-1 text-sm ${mutedClass}`}>
              Switch the active repo, clear the current choice, or remove repos you no longer want in this session.
            </p>
          </div>
          {githubState.selectedRepository && (
            <button
              onClick={handleClearSelectedRepository}
              className={secondaryButtonClass}
            >
              Clear Selection
            </button>
          )}
        </div>

        <div className="space-y-3">
          {githubState.repositories.map((repo) => {
            const isSelected = repo.full_name === githubState.selectedRepository;
            return (
              <div key={repo.id} className={itemClass}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`truncate text-sm font-semibold ${textClass}`}>{repo.full_name}</p>
                      {isSelected && (
                        <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                          Active
                        </span>
                      )}
                    </div>
                    <p className={`mt-1 text-xs ${mutedClass}`}>
                      Default branch: {repo.default_branch || 'main'}
                    </p>
                    {repo.description && !compact && (
                      <p className={`mt-1 text-xs ${mutedClass}`}>{repo.description}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {!isSelected && (
                      <button
                        onClick={() => handleRepositoryChange(repo.full_name)}
                        className={secondaryButtonClass}
                      >
                        Use Repo
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveRepository(repo.full_name)}
                      className={dangerButtonClass}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Website Speed Test & Core Web Vitals Checker | Web Speed Advisor"
        description="Run a free website speed test and Core Web Vitals check. Compare mobile and desktop Lighthouse results, then get AI-guided optimisation fixes your team can apply."
        canonical="https://webspeedadvisor.com/"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Web Speed Advisor',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'Web',
          url: 'https://webspeedadvisor.com/',
          description: 'Free website speed test and Core Web Vitals checker with mobile and desktop Lighthouse results, AI recommendations, implementation snippets, and draft GitHub pull requests for selected frontend fixes.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          },
          featureList: [
            'Website speed test',
            'Core Web Vitals check',
            'Mobile and desktop Lighthouse analysis',
            'AI optimisation recommendations',
            'Implementation snippets',
            'Draft GitHub pull requests'
          ]
        }}
      />
      <Toaster position="top-right" richColors />
      
      <Suspense fallback={null}>
        <SiteContextModal
          isOpen={showContextModal}
          onClose={() => setShowContextModal(false)}
          onSubmit={handleContextSubmit}
          detectedTech={detectedTech}
          detecting={detecting}
          techDetectionMessage={techDetectionMessage}
          resetKey={url}
        />

        <LoadingSnippetModal isOpen={loadingSnippet} contentKind={loadingSnippetKind} />

        <SnippetModal
          isOpen={snippetModal.isOpen}
          onClose={() => setSnippetModal({ ...snippetModal, isOpen: false })}
          snippet={snippetModal.snippet}
          fixTitle={snippetModal.fixTitle}
          techStack={snippetModal.techStack}
          contentKind={snippetModal.contentKind}
        />
      </Suspense>
      
      <section className="hero-section min-h-[65vh] flex flex-col items-center justify-center text-center px-4 py-16">
        <div className="max-w-4xl mx-auto space-y-8">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-200">
            Web Speed Advisor
          </p>
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-primary rounded-2xl">
              <Zap size={48} className="text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black font-chivo tracking-tight text-white leading-tight">
            Website Speed Test for Core Web Vitals and Faster Pages
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 font-inter max-w-2xl mx-auto">
            Run a free website speed test, compare mobile and desktop Lighthouse results, and get AI-prioritised fixes for LCP, CLS, JavaScript, images, and rendering bottlenecks.
          </p>

          <div className="max-w-3xl w-full mx-auto mt-12">
            <div className="flex w-full flex-col sm:flex-row gap-4">
              <input
                type="text"
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter a page URL to analyse (e.g., example.com)"
                className="url-input w-full min-w-0 flex-1 h-[4.5rem] sm:h-16 px-6 text-base leading-6 sm:text-lg bg-white border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 font-mono"
                disabled={loading}
                data-testid="url-input"
              />
              <button
                onClick={handleAnalyzeClick}
                disabled={loading}
                className="analyze-button w-full sm:w-auto h-16 px-6 sm:px-8 bg-primary hover:bg-primary-hover text-white font-bold text-base sm:text-lg rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                data-testid="analyze-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={24} className="animate-spin" />
                    Analysing...
                  </>
                ) : (
                  <>
                    <Zap size={24} />
                    Analyse My Website
                  </>
                )}
              </button>
            </div>
            {analysisError && (
              <div className={`mt-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm text-left ${
                analysisError.type === 'validation' || analysisError.type === 'rate_limit'
                  ? 'border-amber-400/40 bg-amber-500/10 text-amber-100'
                  : 'border-red-400/40 bg-red-500/10 text-red-100'
              }`}>
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <span>{analysisError.message}</span>
                  {['api_busy', 'timeout', 'backend_unavailable'].includes(analysisError.type) && (
                    <button
                      onClick={handleAnalyzeClick}
                      className="mt-2 flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20 transition-colors"
                    >
                      <Loader2 size={12} />
                      Try again
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-300">
              <span className="flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="#22c55e"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                No account needed
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="#22c55e"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Free to use
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="#22c55e"/><path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Mobile &amp; desktop in one run
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200">
                <svg width="14" height="14" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4z" fill="#4285F4"/>
                  <path d="M24 4v20l14.1 14.1C41.6 34.6 44 29.6 44 24c0-11-9-20-20-20z" fill="#34A853"/>
                  <path d="M4 24c0 5.6 2.3 10.6 6 14.2L24 24H4z" fill="#FBBC05"/>
                  <path d="M24 24L9.9 38.2C13.4 41.6 18.4 44 24 44c5.5 0 10.5-2.3 14.1-5.9L24 24z" fill="#EA4335"/>
                </svg>
                Powered by Google PageSpeed Insights
              </span>
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200">
                <Zap size={12} className="text-yellow-300" />
                Google Lighthouse Audits
              </span>
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200">
                AI-Prioritised Fixes
              </span>
            </div>

            <AuthOnboardingCard
              supabaseConfigured={supabaseConfigured}
              authReady={authReady}
              authLoading={authLoading}
              accountContext={accountContext}
              onboardingForm={onboardingForm}
              onboardingSubmitting={onboardingSubmitting}
              onWorkspaceNameChange={(workspaceName) => setOnboardingForm((current) => ({ ...current, workspaceName }))}
              onPrimaryEmailChange={(primaryEmail) => setOnboardingForm((current) => ({ ...current, primaryEmail }))}
              onSignIn={handleGoogleSignIn}
              onSignOut={handleSignOut}
              onSubmitOnboarding={handleCreateTenantOnboarding}
              userHistory={userHistory}
              userHistoryLoading={userHistoryLoading}
              userHistoryLoadingMore={userHistoryLoadingMore}
              userJobsLoading={userJobsLoading}
              loadingHistoryAnalysis={loadingHistoryAnalysis}
              userUsage={userUsage}
              onLoadAnalysis={handleLoadAnalysis}
              onLoadMoreAnalyses={loadMoreAnalyses}
            />
          </div>
        </div>

        {!loading && !result && (
          <div className="pb-16 px-4">
            <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
              What you'll get
            </p>
            <Suspense fallback={null}>
              <ResultPreview />
            </Suspense>
          </div>
        )}
      </section>

      {(loading || loadingRecommendations) && (
        <div data-progress-section>
          <Suspense fallback={null}>
            <AnalysisProgress isAnalyzing={loading || loadingRecommendations} />
          </Suspense>
        </div>
      )}

      {!loading && !result && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-12">
          <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/website-speed-test" className="group bg-white border border-slate-200 rounded-xl p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <h2 className="text-2xl font-bold font-chivo text-slate-900 mb-3">
                Website Speed Test
              </h2>
              <p className="text-slate-700">
                Check how quickly your page loads, how efficiently it renders, and which issues are most likely to hold back rankings, engagement, and conversions.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                Learn about speed testing
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
            <Link to="/core-web-vitals-check" className="group bg-white border border-slate-200 rounded-xl p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <h2 className="text-2xl font-bold font-chivo text-slate-900 mb-3">
                Core Web Vitals Check
              </h2>
              <p className="text-slate-700">
                Review user-experience metrics such as Largest Contentful Paint and Cumulative Layout Shift, plus Lighthouse responsiveness signals that help explain why a page feels slow.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                Understand Core Web Vitals
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
            <Link to="/ai-snippets-draft-prs" className="group bg-white border border-slate-200 rounded-xl p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <h2 className="text-2xl font-bold font-chivo text-slate-900 mb-3">
                Snippets & Draft PRs
              </h2>
              <p className="text-slate-700">
                Move from diagnosis to action with prioritised fixes, implementation-ready snippets, and draft GitHub pull requests for supported low-risk frontend changes.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                See implementation help
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </section>

          <section className="bg-white rounded-xl p-8 shadow-sm">
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
              What This Page Speed Tool Helps You Find
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-700">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Slow rendering paths</h3>
                <p>Spot render-blocking CSS, heavy JavaScript, oversized assets, and other issues that delay the first useful paint.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Core Web Vitals regressions</h3>
                <p>See where loading, stability, and responsiveness issues can hurt page experience and where technical changes are most likely to improve Core Web Vitals outcomes.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Mobile vs desktop gaps</h3>
                <p>Compare device strategies to understand whether the bottleneck is tied to CPU pressure, network constraints, or layout complexity.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Actionable optimisation work</h3>
                <p>Turn raw audit output into a prioritised plan your developers, SEO team, or agency can act on immediately.</p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-xl p-8 shadow-sm">
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
              Built for Search Teams, Developers, and AI Answer Engines
            </h2>
            <div className="space-y-5 text-slate-700">
              <p>
                Web Speed Advisor explains performance issues in clear, source-like language: what failed, which metric it affects, why it matters, and what to change next. That makes the report easier for developers to implement, easier for SEO teams to brief, and easier for AI assistants to summarise accurately.
              </p>
              <p>
                The public guides cover high-intent topics such as website speed testing, Core Web Vitals checks, LCP optimisation, image performance, render-blocking resources, caching, and JavaScript responsiveness. Together, those pages create an internal content path from education to the live speed test.
              </p>
            </div>
          </section>

          <section className="bg-slate-900 text-white rounded-xl p-8 shadow-sm">
            <h2 className="text-3xl font-bold font-chivo mb-6">
              How AI Helps You Prioritise and Apply Fixes
            </h2>
            <div className="space-y-5 text-slate-200">
              <p>
                Instead of dumping raw Lighthouse output, Web Speed Advisor uses AI to turn technical findings into clear, human-readable guidance. The goal is to help you understand not only what failed, but also why it matters and where the fix should be applied.
              </p>
              <p>
                The recommendations are generated from your page speed results, Core Web Vitals signals, and the site context you provide. That allows the tool to prioritise the biggest issues first and frame them in a way that is useful for site owners, marketers, and developers.
              </p>
              <p>
                In practice, that means you get more than a list of warnings. You get contextual suggestions that point to likely causes, explain expected impact, and help your team decide how to apply each improvement with less guesswork. For supported fixes, the product can also generate code snippets and attempt a tightly scoped draft PR.
              </p>
            </div>
          </section>

          <section className="bg-blue-50 border border-blue-200 rounded-xl p-8">
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
              How to Improve Core Web Vitals
            </h2>
            <div className="space-y-6 text-slate-700">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Improve LCP</h3>
                <p>Reduce server response time, preload critical assets, compress large images, and remove render-blocking resources above the fold.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Improve INP</h3>
                <p>Break up long JavaScript tasks, reduce main-thread work, delay non-critical scripts, and simplify interaction-heavy components. In lab diagnostics, Total Blocking Time can help reveal the same responsiveness bottlenecks.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Improve CLS</h3>
                <p>Reserve space for images and embeds, define dimensions early, avoid injecting content above existing content, and stabilise font loading.</p>
              </div>
            </div>
          </section>

          {isLocalGitHubTestMode && (
            <section className="bg-slate-900 text-white rounded-xl p-8 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-3xl font-bold font-chivo mb-2">
                    Developer Tools
                  </h2>
                  <p className="text-slate-300 text-sm max-w-3xl">
                    Use this only for local GitHub setup and manual installation sync before running an analysis.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeveloperTools((current) => !current)}
                  className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
                >
                  {showDeveloperTools ? 'Hide Developer Tools' : 'Show Developer Tools'}
                </button>
              </div>
              {showDeveloperTools && renderLocalGitHubTestPanel()}
            </section>
          )}

          <Suspense fallback={null}>
            <FAQSection faqs={homeFaqs} />
          </Suspense>
        </div>
      )}

      {result && (!loading || loadingRecommendations) && (
        <Suspense fallback={null}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-12" data-results-section>
          <div className="flex justify-center fade-in-up">
            <div className="inline-flex rounded-lg bg-slate-200 p-1" data-testid="strategy-tabs">
              <button
                onClick={() => handleTabChange('mobile')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'mobile'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-mobile"
              >
                <Smartphone size={20} />
                Mobile
              </button>
              <button
                onClick={() => handleTabChange('desktop')}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'desktop'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-desktop"
              >
                <Monitor size={20} />
                Desktop
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6 fade-in-up">
            <PerformanceGauge score={currentData.score} />
            <div className="text-center max-w-2xl">
              <p className="text-sm text-slate-500 font-mono" data-testid="analyzed-url">{result.url}</p>
              <p className="text-xs text-slate-400 mt-1">Strategy: {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</p>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <svg width="12" height="12" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4z" fill="#4285F4"/>
                  <path d="M24 4v20l14.1 14.1C41.6 34.6 44 29.6 44 24c0-11-9-20-20-20z" fill="#34A853"/>
                  <path d="M4 24c0 5.6 2.3 10.6 6 14.2L24 24H4z" fill="#FBBC05"/>
                  <path d="M24 24L9.9 38.2C13.4 41.6 18.4 44 24 44c5.5 0 10.5-2.3 14.1-5.9L24 24z" fill="#EA4335"/>
                </svg>
                Data via Google PageSpeed Insights API
              </div>
              {currentPageSpeedMetadata && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                        PageSpeed Run Details
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {getPageSpeedSourceLabel(currentPageSpeedMetadata)}
                      </p>
                    </div>
                    <button
                      onClick={handleRefreshLiveResult}
                      disabled={loading || refreshingLiveData}
                      className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {refreshingLiveData ? 'Refreshing live run…' : 'Refresh Live Result'}
                    </button>
                  </div>

                  <div className="mt-4 grid gap-3 text-sm text-slate-600 md:grid-cols-2">
                    <div>
                      <p className="font-semibold text-slate-800">Google Fetch Time</p>
                      <p>{formatFetchTime(currentPageSpeedMetadata.fetch_time)}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Final Tested URL</p>
                      <p className="break-all">{currentPageSpeedMetadata.final_url || 'Unknown'}</p>
                    </div>
                    {processingTime && (
                      <>
                        <div>
                          <p className="font-semibold text-slate-800">Scores Ready In</p>
                          <p>{(processingTime.phase1Ms / 1000).toFixed(1)}s</p>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">Total Analysis Time</p>
                          <p>{(processingTime.totalMs / 1000).toFixed(1)}s</p>
                        </div>
                      </>
                    )}
                  </div>

                  {currentPageSpeedMetadata.run_warnings?.length > 0 && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                      {currentPageSpeedMetadata.run_warnings[0]}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-900 text-white rounded-2xl p-6 fade-in-up" style={{ animationDelay: '0.05s' }}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">
                  Implementation Beta
                </p>
                <h2 className="text-2xl font-bold font-chivo">Generate draft PRs for safe, low-risk fixes</h2>
                <p className="text-sm text-slate-300 max-w-2xl">
                  This milestone only allows a narrow set of explainable frontend fixes. Recommendations outside that allowlist stay read-only.
                </p>
              </div>

              <div className="flex flex-col gap-3 min-w-[280px]">
                {!githubState.connected ? (
                  <>
                    <div className="flex gap-2">
                      <button
                        onClick={handleConnectGitHub}
                        disabled={githubConnecting || !githubState.configured}
                        className="flex-1 px-4 py-3 bg-white text-slate-900 font-semibold rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {githubConnecting
                          ? 'Opening GitHub…'
                          : githubState.configured
                            ? 'Connect GitHub to Try Draft PRs'
                            : 'GitHub App not configured'}
                      </button>
                      <button
                        onClick={async () => {
                          setRefreshingGithub(true);
                          await refreshGitHubState(accountContext.accessToken);
                          setRefreshingGithub(false);
                        }}
                        disabled={refreshingGithub}
                        title="Refresh connection status"
                        className="px-3 py-3 bg-slate-700 text-slate-200 rounded-xl hover:bg-slate-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`w-4 h-4 ${refreshingGithub ? 'animate-spin' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs text-slate-300">
                      {githubState.configured
                        ? 'Install the GitHub App on the repository you want to target. The page will update automatically once done.'
                        : 'Set GITHUB_APP_INSTALL_URL or GITHUB_APP_SLUG in the backend to enable the connect flow.'}
                    </p>
                    {syncingInstallation && (
                      <p className="text-xs text-blue-200">Syncing installation details from GitHub...</p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="rounded-xl border border-slate-700 bg-slate-950/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
                        Active Repository
                      </p>
                      {selectedRepository ? (
                        <>
                          <p className="mt-2 text-sm font-semibold text-white">
                            {selectedRepository.full_name}
                          </p>
                          <p className="mt-1 text-xs text-slate-300">
                            Draft PRs will target `{selectedRepository.default_branch || 'main'}`.
                          </p>
                        </>
                      ) : (
                        <p className="mt-2 text-sm text-slate-300">
                          No repository selected. Choose one in the repository manager below.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleRefreshRepositories}
                      className="rounded-xl border border-slate-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                    >
                      {syncingInstallation ? 'Refreshing…' : 'Refresh Repository List'}
                    </button>
                    <button
                      onClick={() => setShowRepositoryManager((current) => !current)}
                      className="rounded-xl border border-slate-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                    >
                      {showRepositoryManager ? 'Hide Repository Manager' : 'Manage Repositories'}
                    </button>
                    {githubState.configured && (
                      <button
                        onClick={handleConnectGitHub}
                        disabled={githubConnecting}
                        className="rounded-xl border border-blue-700 px-3 py-2 text-sm font-semibold text-blue-200 transition-colors hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {githubConnecting ? 'Opening GitHub…' : 'Connect Another Repository'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {implementationLoading && (
              <p className="mt-4 text-sm text-blue-200">Checking which recommendations are safe enough to automate...</p>
            )}

            {result?.url && githubState.connected && selectedRepository && selectedRepositoryMatch && (
              <div className="mt-4 rounded-xl border border-slate-700 bg-slate-950/70 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Repository Match Check
                    </p>
                    <p className="mt-1 text-sm text-white">
                      Selected repo: <span className="font-semibold">{selectedRepository.full_name}</span>
                    </p>
                    <p className="text-sm text-slate-300">
                      Website: <span className="font-semibold">{result.url}</span>
                    </p>
                  </div>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                    selectedRepositoryMatch.score >= 0.9
                      ? 'bg-emerald-100 text-emerald-700'
                      : selectedRepositoryMatch.score >= 0.6
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedRepositoryMatch.label}
                  </span>
                </div>

                <ul className="mt-3 list-disc list-inside space-y-1 text-sm text-slate-300">
                  {selectedRepositoryMatch.reasons.map((reason, index) => (
                    <li key={index}>{reason}</li>
                  ))}
                </ul>

                {bestRepositoryMatch && bestRepositoryMatch.repository.full_name !== selectedRepository.full_name && bestRepositoryMatch.match.score > selectedRepositoryMatch.score && (
                  <p className="mt-3 text-sm text-blue-200">
                    Better detected match available: <span className="font-semibold">{bestRepositoryMatch.repository.full_name}</span> ({bestRepositoryMatch.match.label}).
                  </p>
                )}
              </div>
            )}

            {githubState.connected && showRepositoryManager && renderRepositoryManager({ dark: true, compact: true })}

            {isLocalGitHubTestMode && (
              <div className="mt-4">
                <button
                  onClick={() => setShowDeveloperTools((current) => !current)}
                  className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
                >
                  {showDeveloperTools ? 'Hide Developer Tools' : 'Show Developer Tools'}
                </button>
                {showDeveloperTools && renderLocalGitHubTestPanel()}
              </div>
            )}
          </div>

          <div className="fade-in-up" style={{ animationDelay: '0.1s' }}>
            {loadingRecommendations || !currentData.ai_recommendation ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/3" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
                <div className="mt-4 text-sm text-slate-400 text-center">Generating AI recommendations…</div>
              </div>
            ) : (
            <TopFixCard
              fix={currentData.ai_recommendation.top_fix}
              summary={currentData.ai_recommendation.summary}
              onGenerateSnippet={handleGenerateSnippet}
              onGenerateManualGuidance={() => handleGenerateSnippet(
                currentData.ai_recommendation.top_fix,
                {
                  guidanceMode: getImplementationCandidate(activeTab, 'top_fix')?.guidance_mode || 'manual_guide',
                  implementationCandidate: getImplementationCandidate(activeTab, 'top_fix')
                }
              )}
              implementationCandidate={getImplementationCandidate(activeTab, 'top_fix')}
              implementationJob={implementationJobs[getRecommendationKey(activeTab, 'top_fix')]}
              implementationLoading={implementationLoading}
              githubConnected={githubState.connected}
              repositorySelected={Boolean(githubState.selectedRepository)}
              creatingImplementation={creatingImplementationKey === getRecommendationKey(activeTab, 'top_fix')}
              onConnectGitHub={handleConnectGitHub}
              onGenerateImplementation={() => handleCreateImplementationJob(activeTab, 'top_fix')}
            />
            )}
          </div>

          {nextInsightEntries.length > 0 && (
            <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
                More Insights
              </h2>
              <p className="mb-6 max-w-3xl text-sm text-slate-600">
                These items map directly to PageSpeed insight-style findings and are ranked after the top priority fix.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="next-fixes-container">
                {nextInsightEntries.map(({ fix, index }) => (
                  <NextFixCard 
                    key={index} 
                    fix={fix} 
                    index={index}
                    onGenerateSnippet={handleGenerateSnippet}
                    onGenerateManualGuidance={() => handleGenerateSnippet(
                      fix,
                      {
                        guidanceMode: getImplementationCandidate(activeTab, 'next_fix', index)?.guidance_mode || 'manual_guide',
                        implementationCandidate: getImplementationCandidate(activeTab, 'next_fix', index)
                      }
                    )}
                    implementationCandidate={getImplementationCandidate(activeTab, 'next_fix', index)}
                    implementationJob={implementationJobs[getRecommendationKey(activeTab, 'next_fix', index)]}
                    implementationLoading={implementationLoading}
                    githubConnected={githubState.connected}
                    repositorySelected={Boolean(githubState.selectedRepository)}
                    creatingImplementation={creatingImplementationKey === getRecommendationKey(activeTab, 'next_fix', index)}
                    onConnectGitHub={handleConnectGitHub}
                    onGenerateImplementation={() => handleCreateImplementationJob(activeTab, 'next_fix', index)}
                  />
                ))}
              </div>
            </div>
          )}

          {diagnosticEntries.length > 0 && (
            <div className="fade-in-up" style={{ animationDelay: '0.24s' }}>
              <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-3">
                Diagnostics To Review
              </h2>
              <p className="mb-6 max-w-3xl text-sm text-slate-600">
                These findings explain what is contributing to the slowdown, but they are usually supporting diagnostics rather than standalone auto-fix opportunities.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {diagnosticEntries.map(({ fix, index }) => (
                  <NextFixCard
                    key={`diagnostic-${index}`}
                    fix={fix}
                    index={index}
                    onGenerateSnippet={handleGenerateSnippet}
                    onGenerateManualGuidance={() => handleGenerateSnippet(
                      fix,
                      {
                        guidanceMode: getImplementationCandidate(activeTab, 'next_fix', index)?.guidance_mode || 'manual_guide',
                        implementationCandidate: getImplementationCandidate(activeTab, 'next_fix', index)
                      }
                    )}
                    implementationCandidate={getImplementationCandidate(activeTab, 'next_fix', index)}
                    implementationJob={implementationJobs[getRecommendationKey(activeTab, 'next_fix', index)]}
                    implementationLoading={implementationLoading}
                    githubConnected={githubState.connected}
                    repositorySelected={Boolean(githubState.selectedRepository)}
                    creatingImplementation={creatingImplementationKey === getRecommendationKey(activeTab, 'next_fix', index)}
                    onConnectGitHub={handleConnectGitHub}
                    onGenerateImplementation={() => handleCreateImplementationJob(activeTab, 'next_fix', index)}
                  />
                ))}
              </div>
            </div>
          )}

          {leadCaptureConfig && (
            <div className="fade-in-up" style={{ animationDelay: '0.25s' }}>
              <LeadCaptureCard
                title={leadCaptureConfig.title}
                description={leadCaptureConfig.description}
                email={leadEmail}
                note={leadNote}
                submitting={leadCaptureSubmitting}
                submitted={leadCaptureSubmitted}
                onEmailChange={setLeadEmail}
                onNoteChange={setLeadNote}
                onSubmit={handleLeadCaptureSubmit}
              />
            </div>
          )}

          {Array.isArray(currentData.core_web_vitals) && currentData.core_web_vitals.length > 0 && (
          <div className="fade-in-up" style={{ animationDelay: '0.3s' }}>
            <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-8 text-center">
              Core Web Vitals
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {currentData.core_web_vitals.map((metric, index) => (
                <MetricCard
                  key={index}
                  name={metric.name}
                  value={metric.value}
                  status={metric.status}
                  description={metric.description}
                />
              ))}
            </div>
          </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="flex gap-3">
              <AlertTriangle className="text-blue-600 flex-shrink-0" size={24} />
              <div>
                <h3 className="font-bold text-blue-900 mb-2 font-chivo">About the Metrics</h3>
                <ul className="text-sm text-blue-800 space-y-1 font-inter">
                  <li><strong>LCP (Largest Contentful Paint):</strong> Good: ≤2.5s, Needs Improvement: ≤4s, Poor: &gt;4s</li>
                  <li><strong>CLS (Cumulative Layout Shift):</strong> Good: ≤0.1, Needs Improvement: ≤0.25, Poor: &gt;0.25</li>
                  <li><strong>TBT (Total Blocking Time):</strong> Good: ≤200ms, Needs Improvement: ≤600ms, Poor: &gt;600ms</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        </Suspense>
      )}

      {result && !loading && currentData?.ai_recommendation?.top_fix && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-lg px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Top Fix</p>
            <p className="text-sm font-semibold text-slate-900 truncate">{currentData.ai_recommendation.top_fix.title}</p>
          </div>
          <button
            onClick={() => {
              const topFixCard = document.querySelector('[data-testid="top-fix-card"]');
              if (topFixCard) topFixCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg text-sm transition-colors"
          >
            <Zap size={14} />
            View Fix
          </button>
        </div>
      )}

      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold font-chivo mb-2">Web Speed Advisor</h3>
              <p className="text-slate-400 text-sm">Website speed analysis, AI guidance, and safe implementation help for performance teams.</p>
            </div>
            <div>
              <h4 className="font-bold mb-3">Resources</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/website-speed-test" className="hover:text-white">Website Speed Test</a></li>
                <li><a href="/core-web-vitals-check" className="hover:text-white">Core Web Vitals Check</a></li>
                <li><a href="/ai-snippets-draft-prs" className="hover:text-white">Snippets & Draft PRs</a></li>
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} Web Speed Advisor. All rights reserved.</p>
            <p className="mt-2">Powered by Google PageSpeed Insights & AI</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
